/**
 * The "Send feedback" button on game pages opens a small dialog: a thumbs up
 * or down, a few words, or both. It is a native <dialog>, built on the first
 * click, so it costs the page nothing until someone asks for it.
 */

import { currentGameSlug, newSubmissionId, submit, SubmitError } from './feedback-client.js';
import { reactionButtonsHTML, reactionOf, showReaction } from './reaction-buttons.js';
import { HONEYPOT_FIELD, LIMITS, type Reaction } from './submission.js';

let dialog: HTMLDialogElement | null = null;

function buildDialog(): HTMLDialogElement {
  const element = document.createElement('dialog');
  element.className = 'fb-dialog';
  element.setAttribute('aria-labelledby', 'fb-dialog-title');
  element.innerHTML = `
    <form class="fb-form" novalidate>
      <div class="fb-dialog-head">
        <h2 id="fb-dialog-title" class="fb-dialog-title">Send feedback</h2>
        <button type="button" class="fb-close" data-fb-close aria-label="Close">×</button>
      </div>
      <fieldset class="fb-field">
        <legend class="fb-label">How is this game? <span class="fb-optional">Optional</span></legend>
        <div class="fb-thumbs fb-thumbs-labelled">${reactionButtonsHTML(true)}</div>
      </fieldset>
      <div class="fb-field">
        <label class="fb-label" for="fb-message">Anything to tell us? <span class="fb-optional">Optional</span></label>
        <textarea id="fb-message" class="fb-input" name="message" rows="4" maxlength="${LIMITS.message}"
          placeholder="A bug, an idea, a game you would like to see"></textarea>
      </div>
      <div class="fb-honeypot" aria-hidden="true">
        <label for="fb-${HONEYPOT_FIELD}">Leave this empty</label>
        <input id="fb-${HONEYPOT_FIELD}" name="${HONEYPOT_FIELD}" type="text" tabindex="-1" autocomplete="off">
      </div>
      <p class="fb-status" role="alert"></p>
      <button type="submit" class="modal-btn modal-btn-primary fb-submit">Send</button>
      <p class="fb-note">Want a reply? Use the <a href="/contact">contact page</a>.</p>
    </form>
    <div class="fb-done" role="status" hidden>
      <h2 class="fb-dialog-title">Thanks</h2>
      <p>Your feedback is in.</p>
      <button type="button" class="modal-btn modal-btn-secondary" data-fb-close>Close</button>
    </div>
  `;
  document.body.appendChild(element);

  const form = element.querySelector<HTMLFormElement>('.fb-form');
  const thumbs = element.querySelector('.fb-thumbs');
  const message = element.querySelector<HTMLTextAreaElement>('#fb-message');
  const honeypot = element.querySelector<HTMLInputElement>(`#fb-${HONEYPOT_FIELD}`);
  const status = element.querySelector('.fb-status');
  const sendButton = element.querySelector<HTMLButtonElement>('.fb-submit');
  const done = element.querySelector<HTMLElement>('.fb-done');
  if (!form || !thumbs || !message || !honeypot || !status || !sendButton || !done) {
    throw new Error('Feedback dialog markup is incomplete');
  }

  let reaction: Reaction | undefined;
  // One id per opening: a retry after an error updates the same submission.
  let id = newSubmissionId();

  const reset = (): void => {
    form.reset();
    reaction = undefined;
    showReaction(thumbs, undefined);
    status.textContent = '';
    form.hidden = false;
    done.hidden = true;
    id = newSubmissionId();
  };

  thumbs.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.fb-thumb') : null;
    if (!button) return;
    const picked = reactionOf(button);
    reaction = picked === reaction ? undefined : picked;
    showReaction(thumbs, reaction);
    status.textContent = '';
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const game = currentGameSlug();
    if (!game) return;
    sendButton.disabled = true;
    status.textContent = '';
    submit(
      { kind: 'feedback', id, source: 'feedback-button', game, reaction, message: message.value },
      honeypot.value,
    )
      .then(() => {
        form.hidden = true;
        done.hidden = false;
        done.querySelector<HTMLButtonElement>('[data-fb-close]')?.focus();
      })
      .catch((error: unknown) => {
        status.textContent = error instanceof SubmitError ? error.message : 'Could not send. Try again.';
      })
      .finally(() => {
        sendButton.disabled = false;
      });
  });

  // Close on the × and Close buttons, and on a tap on the backdrop that also
  // started there: a finger already down when the dialog opened would
  // otherwise close it the moment it lifts.
  let pressedOnBackdrop = false;
  element.addEventListener('pointerdown', (event) => {
    pressedOnBackdrop = event.target === element;
  });
  element.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('[data-fb-close]') || (target === element && pressedOnBackdrop)) element.close();
    pressedOnBackdrop = false;
  });
  element.addEventListener('close', () => {
    if (!done.hidden) reset();
  });

  return element;
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-feedback-open]') : null;
  if (!target) return;
  dialog ??= buildDialog();
  dialog.showModal();
});

export {};
